import axios from "axios";
import sourceUrl from "../api/Source.api";
import { jwtDecode } from "jwt-decode";
import { refreshAuthToken } from "../contexts/AuthUtils";
import { InvokeMatrixApi } from "../contexts/WorkspaceUtils";

// 🔹 API Base URL
const API_BASE_URL = sourceUrl;

// 🔹 Base URL (Skip These Requests)
const SKIP_DOMAINS = [
  // "firebaseio.com", 
  // "firebaseapp.com", 
  // "googleapis.com", 
  "google-analytics.com", "hot-update.json", "/api/v1/logs",
  "testingprototype.s3.",
  "static.modalx.ai", "uploads.s3.amazonaws","https://unpkg.com","ffmpeg",
  "amazonaws.com", "https://uploads.dev.modalx.ai/", "blob:"
];

// 🔹 Get Auth State
const getAuthToken = () => localStorage.getItem("authToken");
const getTempToken = () => localStorage.getItem("tempToken");
const getUserId = () => localStorage.getItem("userId");
const isLoggedIn = () => localStorage.getItem("isLoggedIn") === "true";

// ✅ Function to Validate Token
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 > Date.now();
  } catch (error) {
    console.error("Invalid token:", error);
    return false;
  }
};

// ✅ Function to Get a Valid Token (Refresh If Needed)
const getValidAuthToken = async () => {
  if (!isLoggedIn()) return null;
  
  let authToken = getAuthToken();
  if (!isTokenValid(authToken)) {
    try {
      authToken = await refreshAuthToken();
    } catch (error) {
      console.error("Failed to refresh token:", error);
      throw error;
    }
  }
  return authToken;
};

// ✅ Check if Request Should Skip Auth
const shouldSkipAuth = (url) => {
  if (!url) return true;
  return SKIP_DOMAINS.some(domain => url.includes(domain));
};

// ✅ Ensure Fetch is Available
if (!window.fetch || typeof window.fetch !== "function") {
  console.warn("⚠️ window.fetch is missing, restoring original fetch!");
  window.fetch = window.__originalFetch || fetch;
}

// ✅ Store Original Fetch Safely
window.__originalFetch = window.__originalFetch || window.fetch;

// ✅ Override Fetch (Track API Calls)
window.fetch = async (url, options = {}) => {
  try {
    if (!window.__originalFetch) {
      throw new Error("window.fetch is unavailable.");
    }

    // Skip requests that don't need auth
    if (shouldSkipAuth(url)) {
      return window.__originalFetch(url, options);
    }

    // Only proceed with token logic if logged in
    const authToken = isLoggedIn() ? await getValidAuthToken() : null;
    const tempToken = getTempToken();
    const userId = getUserId();

    const headers = {
      // "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(tempToken ? { "X-Admin-Token": `{Bearer ${tempToken}}` } : {}),
      ...options.headers,
    };

    const response = await window.__originalFetch(
      url.startsWith("http") ? url : `${API_BASE_URL}${url}`,
      { ...options, headers }
    );

    // Track API Response if logged in
    const clonedResponse = response.clone();
    const jsonData = await clonedResponse.json();
    await InvokeMatrixApi( (url.includes("firebaseapp.com") || url.includes("googleapis.com") || url.includes("firebaseio.com")) ? jsonData : {}, response, options?.method, null, url, options?.body || {}, userId);

    return response;
  } catch (error) {
    const userId = getUserId();
    console.error("Fetch error:", error);

    if (userId && isLoggedIn() && !url.includes("/v1/logs")) {
      await InvokeMatrixApi(error, error, options?.method, null, url, options?.body, userId);
    }

    throw error;
  }
};

// ✅ Configure Axios Globally
axios.defaults.baseURL = API_BASE_URL;
// axios.defaults.headers.common["Content-Type"] = "application/json";

// 🔹 Axios Request Interceptor
axios.interceptors.request.use(async (config) => {
  if (shouldSkipAuth(config.url)) {
    return config;
  }

  // Only proceed with token logic if logged in
  if (isLoggedIn()) {
    try {
      const authToken = await getValidAuthToken();
      const tempToken = getTempToken();

      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }
      if (tempToken) {
        config.headers["X-Admin-Token"] = `{Bearer ${tempToken}}`;
      }
    } catch (error) {
      console.error("Token validation failed:", error);
      throw error;
    }
  }

  return config;
});

// 🔹 Axios Response Interceptor
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error("Axios error:", error);

    const userId = getUserId();
    if (userId && isLoggedIn()) {
      await InvokeMatrixApi(error, error.response, error.config?.method, null, error.config?.url, error.config?.data, userId);
    }

    return Promise.reject(error);
  }
);